/**
 * Client-side mirrors of the backend payout-sender bank-material validators
 * (US-3338). These exist to give the operator immediate feedback in the create
 * dialog; the backend (IbanValidator, BicValidator, DanishCvr) remains the
 * authority and re-validates every field. Keep the two in lock-step — a rule
 * loosened here without the backend lets a malformed value reach the secret
 * store.
 */

const stripAndUpper = (value: string): string =>
    value.replace(/\s/g, '').toUpperCase();

export const normaliseIban = stripAndUpper;
export const normaliseBic = stripAndUpper;

// A CVR carries no letters, so casing is irrelevant — strip whitespace only.
export const normaliseCvr = (value: string): string => value.replace(/\s/g, '');

// A Danish CVR is exactly 8 digits, regardless of the account's currency:
// house accounts are Danske Bank accounts owned by a Danish legal entity for
// every supported currency.
export const isValidCvr = (cvr: string): boolean => /^\d{8}$/.test(cvr);

/**
 * ISO 13616 structure + mod-97 checksum, digit-by-digit to match the backend's
 * BigInteger-free remainder walk (letters weigh two positions, digits one).
 */
export const isValidIban = (iban: string): boolean => {
    const normalised = normaliseIban(iban);
    if (normalised.length < 4 || normalised.length > 34) {
        return false;
    }
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]*$/.test(normalised)) {
        return false;
    }

    const rotated = normalised.slice(4) + normalised.slice(0, 4);
    let remainder = 0;
    for (const char of rotated) {
        const code = char.charCodeAt(0);
        if (code >= 48 && code <= 57) {
            remainder = (remainder * 10 + (code - 48)) % 97;
        } else {
            // A=10 .. Z=35, contributes two decimal positions.
            remainder = (remainder * 100 + (code - 55)) % 97;
        }
    }
    return remainder === 1;
};

/**
 * ISO 9362 shape check: 8 or 11 alphanumeric chars with alphabetic country
 * code at positions 5–6 (0-indexed 4–5). Does not consult a BIC directory.
 */
export const isValidBic = (bic: string): boolean => {
    const normalised = normaliseBic(bic);
    if (normalised.length !== 8 && normalised.length !== 11) {
        return false;
    }
    if (!/^[A-Z0-9]+$/.test(normalised)) {
        return false;
    }
    return /[A-Z]/.test(normalised[4]) && /[A-Z]/.test(normalised[5]);
};
